import { getToken, setToken, removeToken, generateToken } from './tokens';
import { getSuperAdminSecret } from './superAdmin';

// The server answers "ping" with "pong" without even waking up the game. If the answer doesn't
// come, the connection is dead (e.g. after a network change) even if the browser hasn't noticed.
const PING_INTERVAL = 20 * 1000;
const PONG_TIMEOUT = 10 * 1000;
// After the tab becomes visible again, the device wakes up or goes back online, check quicker
const WAKE_PONG_TIMEOUT = 4 * 1000;
const CONNECT_TIMEOUT = 10 * 1000;
const RECONNECT_DELAYS = [250, 1000, 2000, 4000, 8000];
const CLOSE_GAME_DOES_NOT_EXIST = 4404;
const CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;

// One WebSocket connection to one game. Every (re)connect starts with a snapshot of the whole
// game, after which the server sends every change, so the local copy can't drift away from the
// server's. Requests that might not have reached the server before a disconnect are resent.
export default class GameConnection {
    constructor(code) {
        this.code = code;
        this.socket = null;
        this.closed = false;
        this.synced = false;
        this.attempt = 0;
        this.game = null;
        this.you = { playerId: null, superAdmin: false };
        this.presence = new Set();
        this.error = null;
        this.syncId = 0;
        this.requests = new Map();
        this.nextRequestId = 1;
        this.pendingActions = [];
        this.optimisticOptions = null;
        this.listeners = new Set();
        this.rematchListeners = new Set();
        this.onWake = this.onWake.bind(this);

        if (!CODE_PATTERN.test(code)) {
            this.error = 'GAME_DOES_NOT_EXIST';
            this.view = this.buildView();

            return;
        }

        this.view = this.buildView();

        document.addEventListener('visibilitychange', this.onWake);
        window.addEventListener('online', this.onWake);
        window.addEventListener('focus', this.onWake);

        this.connect();
    }

    subscribe(listener) {
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    onRematch(listener) {
        this.rematchListeners.add(listener);

        return () => {
            this.rematchListeners.delete(listener);
        };
    }

    close() {
        this.closed = true;

        document.removeEventListener('visibilitychange', this.onWake);
        window.removeEventListener('online', this.onWake);
        window.removeEventListener('focus', this.onWake);

        this.dropSocket();
        clearTimeout(this.reconnectTimer);

        // Requests that are still open are never settled, their components are gone anyway
        this.requests.clear();
        this.listeners.clear();
        this.rematchListeners.clear();
    }

    // ---- Connection handling ---------------------------------------------------------------

    connect() {
        if (this.closed) {
            return;
        }

        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const socket = new WebSocket(`${protocol}//${window.location.host}/api/games/${this.code}/socket`);

        this.socket = socket;
        this.connectTimer = setTimeout(() => this.reconnectNow(), CONNECT_TIMEOUT);

        socket.onopen = () => {
            this.send({
                type: 'hello',
                token: getToken(this.code),
                superAdminSecret: getSuperAdminSecret(),
            });
            this.startHeartbeat();
        };

        socket.onmessage = (event) => this.onMessage(socket, event.data);
        // An error event is always followed by a close event
        socket.onclose = (event) => this.onClose(socket, event.code);
    }

    onClose(socket, code) {
        if (socket !== this.socket) {
            return;
        }

        this.dropSocket();

        if (code === CLOSE_GAME_DOES_NOT_EXIST) {
            this.error = 'GAME_DOES_NOT_EXIST';
            this.emit();

            return;
        }

        this.emit();
        this.scheduleReconnect();
    }

    // Forgets the current socket without waiting for the browser to close it, which can take
    // a long time if the connection died silently.
    dropSocket() {
        const { socket } = this;

        clearTimeout(this.connectTimer);
        this.stopHeartbeat();

        if (!socket) {
            return;
        }

        this.socket = null;
        this.synced = false;

        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;

        try {
            socket.close();
        } catch (error) {
            // Already closed
        }

        // Requests that can't safely be sent twice fail. All others are resent after reconnecting.
        this.requests.forEach((request, requestId) => {
            if (request.socket === socket && !request.retry) {
                this.requests.delete(requestId);
                this.settle(request, new Error('DISCONNECTED'));
            }
        });
    }

    scheduleReconnect() {
        if (this.closed || this.reconnectTimer) {
            return;
        }

        const delay = RECONNECT_DELAYS[Math.min(this.attempt, RECONNECT_DELAYS.length - 1)];

        this.attempt += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay * (0.5 + Math.random()));
    }

    reconnectNow() {
        const wasConnected = this.synced;

        this.dropSocket();
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.attempt = 0;

        if (wasConnected) {
            this.emit();
        }

        this.scheduleReconnect();
    }

    startHeartbeat() {
        this.stopHeartbeat();
        this.lastTick = Date.now();
        this.heartbeat = setInterval(() => {
            // A much longer gap than the interval means the device was asleep
            const wasAsleep = Date.now() - this.lastTick > PING_INTERVAL * 2;

            this.lastTick = Date.now();
            this.ping(wasAsleep ? WAKE_PONG_TIMEOUT : PONG_TIMEOUT);
        }, PING_INTERVAL);
    }

    stopHeartbeat() {
        clearInterval(this.heartbeat);
        clearTimeout(this.pongTimer);
        this.pongTimer = null;
    }

    ping(timeout) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN || this.pongTimer) {
            return;
        }

        this.socket.send('ping');
        this.pongTimer = setTimeout(() => {
            this.pongTimer = null;
            this.reconnectNow();
        }, timeout);
    }

    onWake() {
        if (this.closed || document.visibilityState === 'hidden') {
            return;
        }

        if (this.socket) {
            this.ping(WAKE_PONG_TIMEOUT);

            return;
        }

        // Waiting for the next reconnect attempt, so don't wait any longer
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.attempt = 0;
        this.connect();
    }

    send(message) {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(message));
        }
    }

    // ---- Incoming messages -----------------------------------------------------------------

    onMessage(socket, data) {
        if (socket !== this.socket) {
            return;
        }

        if (data === 'pong') {
            clearTimeout(this.pongTimer);
            this.pongTimer = null;

            return;
        }

        let message;

        try {
            message = JSON.parse(data);
        } catch (error) {
            return;
        }

        switch (message.type) {
            case 'snapshot':
                this.applySnapshot(message);
                break;
            case 'players':
                this.game = { ...this.game, players: message.players };
                break;
            case 'options':
                this.game = { ...this.game, options: message.options };
                break;
            case 'actions':
                // Can't happen on a working connection, but if it does, start over
                if (message.from !== this.game.actions.length) {
                    this.send({ type: 'sync' });

                    return;
                }

                this.game = {
                    ...this.game,
                    status: message.status,
                    actions: [...this.game.actions, ...message.actions],
                };
                this.dropConfirmedActions();
                break;
            case 'presence':
                this.presence = new Set(message.connected);
                break;
            case 'rematch':
                this.followRematch(message.code, message.token);
                break;
            case 'reply':
                this.handleReply(message);
                break;
            case 'error':
                this.error = message.error;
                break;
            default:
                return;
        }

        this.emit();
    }

    applySnapshot({ game, connected, you }) {
        clearTimeout(this.connectTimer);

        this.game = game;
        this.presence = new Set(connected);
        this.you = { playerId: you.playerId, superAdmin: you.superAdmin };
        this.synced = true;
        this.attempt = 0;
        this.error = null;
        // Lets the UI know that this isn't just another change, see Live.jsx
        this.syncId += 1;

        this.dropConfirmedActions();

        this.requests.forEach((request, requestId) => {
            if (request.socket === this.socket) {
                return;
            }

            // Actions the server got before the connection broke don't need to be sent again
            if (request.actionIds && this.areConfirmed(request.actionIds)) {
                this.requests.delete(requestId);
                this.settle(request, null);

                return;
            }

            request.socket = this.socket;
            this.send(request.message);
        });

        // A rematch was started while this player was away
        if (game.nextCode && you.nextToken) {
            this.followRematch(game.nextCode, you.nextToken);
        }
    }

    handleReply({ requestId, result, error }) {
        const request = this.requests.get(requestId);

        if (!request) {
            return;
        }

        this.requests.delete(requestId);
        this.settle(request, error ? new Error(error) : null, result);
    }

    followRematch(code, token) {
        // The notice can arrive more than once, e.g. if the request was resent
        if (this.rematchCode === code) {
            return;
        }

        this.rematchCode = code;

        if (token) {
            setToken(code, token);
        }

        this.rematchListeners.forEach((listener) => listener(code));
    }

    areConfirmed(actionIds) {
        return actionIds.every((id) => this.game.actions.some((action) => action.id === id));
    }

    dropConfirmedActions() {
        this.pendingActions = this.pendingActions.filter(({ actions }) => (
            !this.areConfirmed(actions.map(({ id }) => id))
        ));
    }

    // ---- Requests --------------------------------------------------------------------------

    // `retry`: the request is resent if the connection breaks before the reply arrives, so the
    // server has to handle receiving it twice.
    request(type, payload, { retry = false, actionIds, onSettled } = {}) {
        const requestId = this.nextRequestId;

        this.nextRequestId += 1;

        return new Promise((resolve, reject) => {
            const request = {
                message: { ...payload, type, requestId },
                retry,
                actionIds,
                onSettled,
                resolve,
                reject,
                socket: null,
            };

            this.requests.set(requestId, request);

            // Otherwise it's sent as soon as the connection is back
            if (this.synced) {
                request.socket = this.socket;
                this.send(request.message);
            }
        });
    }

    settle(request, error, result) {
        if (request.onSettled) {
            request.onSettled(error);
        }

        if (error) {
            request.reject(error);
        } else {
            request.resolve(result);
        }
    }

    // Shows the actions right away and takes them back if the server rejects them
    pushActions(actions) {
        const createdAt = new Date().toISOString();
        const pending = {
            actions: actions.map(({ id, type, payload }) => ({
                id,
                type,
                payload,
                randomSeed: null,
                createdAt,
                player: { id: this.you.playerId },
            })),
        };
        const actionIds = actions.map(({ id }) => id);

        this.pendingActions = [...this.pendingActions, pending];
        this.emit();

        return this.request('pushActions', { actions: actions.map(({ id, type, payload }) => ({ id, type, payload })) }, {
            retry: true,
            actionIds,
            onSettled: (error) => {
                this.pendingActions = this.pendingActions.filter((item) => item !== pending);

                // Something is off with the local state, so get a fresh copy
                if (error || !this.areConfirmed(actionIds)) {
                    this.send({ type: 'sync' });
                }

                this.emit();
            },
        });
    }

    changeOptions(options) {
        const optimisticOptions = { options };

        this.optimisticOptions = optimisticOptions;
        this.emit();

        return this.request('changeOptions', { options }, {
            retry: true,
            onSettled: () => {
                // Only if no other change was made in the meantime
                if (this.optimisticOptions === optimisticOptions) {
                    this.optimisticOptions = null;
                    this.emit();
                }
            },
        });
    }

    join(name, color) {
        // Clicking twice must not replace the token of the request that is already on its way
        if (!this.joinPromise) {
            this.joinPromise = this.sendJoin(name, color).finally(() => {
                this.joinPromise = null;
            });
        }

        return this.joinPromise;
    }

    // The token is stored before the request is sent, so even if the reply gets lost, the player
    // is recognized after reconnecting.
    async sendJoin(name, color) {
        const token = generateToken();

        setToken(this.code, token);

        try {
            const { playerId } = await this.request('join', { name, color, token }, { retry: true });

            this.you = { ...this.you, playerId };
            this.emit();
        } catch (error) {
            if (getToken(this.code) === token) {
                removeToken(this.code);
            }

            throw error;
        }
    }

    start() {
        return this.request('start', {}, { retry: true });
    }

    rematch() {
        return this.request('rematch', {}, { retry: true });
    }

    undo() {
        return this.request('undo', {});
    }

    createDummyPlayer() {
        return this.request('createDummyPlayer', {});
    }

    async becomePlayer(playerId) {
        const { token } = await this.request('becomePlayer', { playerId }, { retry: true });

        setToken(this.code, token);
        this.you = { ...this.you, playerId };
        this.emit();
    }

    // ---- State for the UI ------------------------------------------------------------------

    emit() {
        this.view = this.buildView();
        this.listeners.forEach((listener) => listener(this.view));
    }

    buildView() {
        if (!this.game) {
            return { game: null, player: null, superAdmin: false, connected: false, error: this.error };
        }

        const pendingActions = this.pendingActions.flatMap(({ actions }) => actions);
        const players = this.game.players.map((player) => ({
            ...player,
            connected: player.id === this.you.playerId ? this.synced : this.presence.has(player.id),
        }));
        const game = {
            ...this.game,
            options: this.optimisticOptions?.options || this.game.options,
            players,
            actions: pendingActions.length > 0 ? [...this.game.actions, ...pendingActions] : this.game.actions,
            syncId: this.syncId,
        };

        return {
            game,
            player: players.find(({ id }) => id === this.you.playerId) || null,
            superAdmin: this.you.superAdmin,
            connected: this.synced,
            error: this.error,
        };
    }
}
